import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faUser,
  faRobot,
  faWindowMaximize,
  faWindowRestore,
  faWindowMinimize,
} from "@fortawesome/free-solid-svg-icons";

const AiIcdChat = ({
  isOpen,
  onClose,
  messages = [],
  onSend,
  loading = false,
  onClear,
  title = "AI ICD Search",
  placeholder = "Example Ask: What is the ICD code for UPPER GI BLEEDING due to BLEEDING PEPTIC ULCER?",
  initialInput = "",
  initialSize = "medium",
}) => {
  const [input, setInput] = useState(initialInput || "");
  const [size, setSize] = useState(initialSize || "medium");
  const endRef = useRef(null);

  useEffect(() => {
    setInput(initialInput || "");
  }, [initialInput]);

  useEffect(() => {
    // scroll to bottom on messages change
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, size]);

  const escapeHtml = (unsafe) => {
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const markdownToHtml = (md) => {
    if (!md) return "";
    let out = escapeHtml(md);
    out = out.replace(/```([\s\S]*?)```/g, (_, code) =>
      `<pre class=\"rounded bg-gray-900 text-white p-3 overflow-auto\"><code>${escapeHtml(code)}</code></pre>`
    );
    out = out.replace(/`([^`]+)`/g, "<code class=\"px-1 bg-gray-200 rounded text-sm\">$1</code>");
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    out = out.replace(/\*([^*]+)\*/g, "<em>$1</em>");

    const lines = out.split(/\r?\n/);
    let inList = false;
    let processed = lines
      .map((line) => {
        if (/^\s*[-*]\s+/.test(line)) {
          if (!inList) {
            inList = true;
            return `<ul class=\"list-disc list-inside\"><li>${line.replace(/^\s*[-*]\s+/, "")}</li>`;
          }
          return `<li>${line.replace(/^\s*[-*]\s+/, "")}</li>`;
        } else {
          if (inList) {
            inList = false;
            return `</ul><p>${line}</p>`;
          }
          return `<p>${line}</p>`;
        }
      })
      .join("");
    if (inList) processed += "</ul>";
    return processed.replace(/<p><\/p>/g, "");
  };

  const handleSend = () => {
    if (!input?.trim()) return;
    onSend && onSend(input);
    setInput("");
  };

  return (
    <AnimateAside isOpen={isOpen} size={size}>
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
            <FontAwesomeIcon icon={faBrain} />
          </div>
          <h4 className="text-sm font-semibold">{title}</h4>
        </div>
        <div className="flex items-center space-x-2">
          {size !== "minimized" && (
            <button onClick={() => setSize("minimized")} className="text-xs px-2 py-1 rounded hover:bg-gray-100" title="Minimize">
              <FontAwesomeIcon icon={faWindowMinimize} />
            </button>
          )}
          {size !== "maximized" ? (
            <button onClick={() => setSize("maximized")} className="text-xs px-2 py-1 rounded hover:bg-gray-100" title="Maximize">
              <FontAwesomeIcon icon={faWindowMaximize} />
            </button>
          ) : (
            <button onClick={() => setSize("medium")} className="text-xs px-2 py-1 rounded hover:bg-gray-100" title="Restore">
              <FontAwesomeIcon icon={faWindowRestore} />
            </button>
          )}
          <button onClick={onClear} className="text-xs px-2 py-1 rounded hover:bg-gray-100" title="Clear messages">
            Clear
          </button>
          <button onClick={onClose} className="text-xs px-2 py-1 rounded hover:bg-gray-100">
            Close
          </button>
        </div>
      </div>

      <div className="flex-1 p-3 overflow-auto space-y-3">
        {messages && messages.length === 0 && <div className="text-sm text-gray-500">No messages yet. Ask about an ICD code.</div>}

        {messages.map((m) => {
          const isUser = m.role === "user";
          return (
            <div key={m.id} className={`mb-3 flex ${isUser ? "justify-end" : "justify-start"}`}>
              {!isUser && (
                <div className="mr-3">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faRobot} />
                  </div>
                </div>
              )}

              <div className={`max-w-[78%] p-3 rounded-lg text-sm ${isUser ? "bg-indigo-600 text-white" : "bg-white text-gray-800 shadow-sm"}`}>
                {m.text && (isUser ? <div className="whitespace-pre-wrap">{m.text}</div> : <div dangerouslySetInnerHTML={{ __html: markdownToHtml(m.text) }} />)}
              </div>

              {isUser && (
                <div className="ml-3">
                  <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-indigo-600">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div className="mb-3 flex justify-start items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-white">
              <FontAwesomeIcon icon={faRobot} />
            </div>
            <div className="bg-white text-gray-800 p-3 rounded-lg shadow-sm">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 bg-gray-400 rounded-full animate-pulse" />
                <span className="h-2 w-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: "120ms" }} />
                <span className="h-2 w-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: "240ms" }} />
              </div>
              <span className="text-xs opacity-75 block mt-2">AI is thinking...</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border rounded text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button type="button" onClick={handleSend} className="px-3 py-2 bg-indigo-600 text-white rounded text-sm">
            Send
          </button>
        </div>
      </div>
    </AnimateAside>
  );
};

const AnimateAside = ({ isOpen, size, children }) => {
  if (!isOpen) return null;
  return (
    <motion.aside
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className={`fixed right-6 top-16 ${
        size === "maximized" ? "w-[65vw] h-[75vh]" : size === "minimized" ? "w-80 h-[40vh]" : "w-96 h-[70vh]"
      } max-w-full bg-white border shadow-lg rounded-lg z-50 flex flex-col`}
    >
      {children}
    </motion.aside>
  );
};

export default AiIcdChat;
